/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { Button } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import classnames from 'classnames';

/**
 * Class names used in component.
 */
const componentClassName = 'msc-refiner-toggle';
const expandedComponentClassName = `${componentClassName}__toggle_expanded`;
const collapsedComponentClassName = `${componentClassName}__toggle_collapsed`;

/**
 * IRefinerToggleComponent interface.
 */
interface IRefinerToggleComponent extends IComponent<IRefinerToggleProps> {}

/**
 * IRefinerToggleProps interface.
 */
export interface IRefinerToggleProps extends IComponentProps<{}> {
    className?: string;
    text?: string;
    ariaLabel?: string;
    isDisabled?: boolean;
    isExpanded?: boolean;
    onClick?(): void;
}

/**
 * RefinerToggle component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const RefinerToggleFunctionalComponent: React.FC<IRefinerToggleProps> = (props: IRefinerToggleProps) => {
    const { className, text, ariaLabel, isDisabled, isExpanded, onClick } = props;

    return (
        <Button
            className={classnames(className, isExpanded ? expandedComponentClassName : collapsedComponentClassName)}
            disabled={isDisabled}
            aria-label={ariaLabel ?? text}
            aria-expanded={!!isExpanded}
            aria-disabled={isDisabled}
            onClick={onClick}
        >
            {text}
        </Button>
    );
};

/**
 * RefinerToggle component.
 */
export const RefinerToggleComponent: React.FunctionComponent<IRefinerToggleProps> = msdyn365Commerce.createComponent<
    IRefinerToggleComponent
>('Refiner-Toggle', {
    component: React.memo(RefinerToggleFunctionalComponent)
});
